import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Coins, Printer } from 'lucide-react';
import { orderStatusColors } from '@/lib/ui-tokens';
import { useApi } from '@/Hooks/useApi';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Icon } from '@/components/ui/icon';
import { LoadingState } from '@/components/ui/states';
import { TableWrap, Table, THead, TH, TR, TD } from '@/components/ui/table';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { DetailHeader, SummaryRow, SummaryRule } from '@/components/ui/detail';
import { Timeline } from '@/components/ui/timeline';
import PrintableCheck from '@/Pages/Billing/PrintableCheck';
import type { Check } from '@/Pages/Billing/lib';
import { CogsDetail } from './CogsDetail';
import { orderTimeline, totalsBlock, type OrderDetailRow } from '../orderDetail';
import { formatDateTime, formatQuantity, formatTime } from '../shared';

/** `GET /tables/:id` — only the two columns a label needs. */
interface TableRow {
  id: string;
  number: string;
  name: string | null;
}

interface Props {
  order: OrderDetailRow;
  /** The takeaway lane shows `takeawayStatus`; the dine-in lane shows `status`. */
  lifecycleStatus: string;
  isTakeaway: boolean;
  /** The order's own action cluster, built and permission-gated by the board. */
  actions: React.ReactNode;
  onBack: () => void;
}

/**
 * ## 64:8 — one order, in full
 *
 * The frame is a routed page (`Orders > O-260821-0025`); the app has flat list
 * routes only and `Routes.tsx` belongs to no group, so this is the same detail
 * rendered in place of the board, with the trail as the way back. Everything
 * else follows the drawing: line items and their totals on the left, the three
 * summary cards on the right.
 *
 * ### What the frame draws that an Order cannot answer, and what happens instead
 *
 * - **The four-line totals block** is a *Check* projection. `Order` carries
 *   `subtotal` and `total` only; tax, discount, service and tip are computed on
 *   the check from `Charge` rows, and `Order.checkId` is nullable. So the block
 *   is filled from `GET /checks/:id` when there is a check and the reader holds
 *   `checks.view`, and honestly drops to two rows when there is not — see
 *   `totalsBlock`. Service and tip are kept even though the frame omits them,
 *   because dropping a FEE or TIP charge would print arithmetic that does not
 *   add up.
 * - **"Tax (15%)"** has no rate to read. There is no tax entity anywhere in the
 *   product — `Check.taxTotal` is the sum of each charge's own `taxAmount`. The
 *   row is labelled `Tax` and shows the amount.
 * - **Payment Info** hangs off the check too, and arrives with it, so this
 *   screen needs `checks.view` rather than a second `payments.view` read.
 * - **"Guest Type: Guest (walk-in)"** has no backing field and no derivation
 *   rule anywhere in either repo, so it is not drawn.
 * - **The five-step Timeline** has no endpoint. `placedAt` and `completedAt`
 *   are on the order; the middle of the rail is aggregated from the lines'
 *   own stamps, and the card says so.
 * - **Void** is drawn unconditionally on a SERVED order. The gate stays where
 *   it was — `canTransition(ORDER_TRANSITIONS, …)` plus the reason dialog the
 *   route requires — and lives in the cluster the board passes down.
 */
export function OrderDetailPanel({
  order,
  lifecycleStatus,
  isTakeaway,
  actions,
  onBack,
}: Props) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const { format: money } = useCurrency(order.branchId || undefined);
  const [costLine, setCostLine] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);

  // Mirrors the server decorator on `GET /inventory/reports/cogs/:orderItemId`.
  const canCost = can('inventory.view');
  const canReadCheck = can('checks.view');
  const canReadTables = can('tables.view');

  const check = useApi<Check>(
    canReadCheck && order.checkId ? `/checks/${order.checkId}` : null,
  );
  const table = useApi<TableRow>(
    canReadTables && order.session?.tableId ? `/tables/${order.session.tableId}` : null,
  );

  const totals = totalsBlock(order, check.data);
  const steps = orderTimeline(order);
  // The method to name is the one money actually arrived by, so a settled
  // payment wins over a PENDING authorization sitting beside it.
  const payments = check.data?.payments ?? [];
  const payment = payments.find((row) => row.paidAt) ?? payments[0] ?? null;

  const columns = 6 + (canCost ? 1 : 0);

  /** Rendered only when non-zero, so a check with no fee grows no empty row. */
  const nonZero = (value: string | null): boolean =>
    value !== null && Number(value) !== 0;

  return (
    <div className="space-y-4">
      <Breadcrumb
        label={t('nav.orders')}
        trail={[{ label: t('nav.orders'), onClick: onBack }]}
        current={order.number}
      />

      <DetailHeader
        title={t('operations.orders.detailTitle', {
          number: order.number,
        })}
        chips={
          <>
            <Badge tone={toneForStatus(lifecycleStatus)}>
              {isTakeaway
                ? t(`operations.takeawayStatus.${lifecycleStatus}`)
                : t(`operations.orderStatus.${lifecycleStatus}`)}
            </Badge>
            {/* `FulfillmentType` is not a status — it must never reach
                `toneForStatus`, which is exhaustive over the status enums and
                would have to gain a member that is not one. */}
            <Badge tone="neutral">{t(`order.fulfillment.${order.fulfillmentType}`)}</Badge>
          </>
        }
        subtitle={[
          formatDateTime(order.placedAt ?? order.createdAt, i18n.language),
          order.scheduledFor &&
            t('operations.orders.scheduledFor', {
              time: formatTime(order.scheduledFor, i18n.language),
            }),
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <>
            {/* There is no order-level print. `PrintableCheck` is the bill, and
                it needs the check this order was charged to. */}
            {canReadCheck && check.data && (
              <Button variant="outline" size="sm" onClick={() => setPrinting(true)}>
                <Icon as={Printer} />
                {t('billing.print.print')}
              </Button>
            )}
            {actions}
          </>
        }
      />

      <div className="grid gap-4 [&>*]:min-w-0 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:items-start">
        <Card>
          <CardHeader>
            <CardTitle>
              {t('operations.orders.lineItems')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {order.notes && (
              <p className="text-sm text-ink-muted">
                {t('operations.orders.notes')}: {order.notes}
              </p>
            )}

            {!order.items?.length ? (
              <p className="text-sm text-ink-muted">{t('operations.orders.noItems')}</p>
            ) : (
              <TableWrap className="border-0 bg-transparent">
                <Table>
                  <THead>
                    <TR>
                      <TH>{t('operations.orders.item')}</TH>
                      <TH className="text-end">{t('billing.print.qty')}</TH>
                      <TH className="text-end">
                        {t('operations.orders.unitPrice')}
                      </TH>
                      <TH>{t('catalog.tabs.modifiers')}</TH>
                      <TH>{t('operations.orders.itemStatus')}</TH>
                      <TH className="text-end">{t('common.total')}</TH>
                      {canCost && (
                        <TH className="text-end">{t('operations.orders.cogs.column')}</TH>
                      )}
                    </TR>
                  </THead>
                  <tbody>
                    {order.items.map((item) => (
                      <Fragment key={item.id}>
                        <TR>
                          <TD>
                            <div className="font-medium">{item.name}</div>
                            {item.notes && (
                              <div className="text-xs text-amber-700 dark:text-amber-400">
                                {item.notes}
                              </div>
                            )}
                          </TD>
                          <TD className="text-end tabular-nums">
                            {formatQuantity(item.quantity)}
                          </TD>
                          <TD className="text-end tabular-nums">{money(item.unitPrice)}</TD>
                          <TD className="text-sm text-ink-muted">
                            {item.modifiers?.length
                              ? item.modifiers.map((m) => `${m.name} ×${m.quantity}`).join(' · ')
                              : '—'}
                          </TD>
                          <TD>
                            <span className="inline-flex items-center gap-2">
                              <span
                                aria-hidden
                                className="h-2 w-2 shrink-0 rounded-full"
                                style={{
                                  background:
                                    orderStatusColors[
                                      item.status as keyof typeof orderStatusColors
                                    ] ?? '#94a3b8',
                                }}
                              />
                              <Badge tone={toneForStatus(item.status)}>
                                {t(`operations.itemStatus.${item.status}`)}
                              </Badge>
                            </span>
                            <div className="mt-1 text-xs text-ink-muted">
                              {[
                                item.preparingAt &&
                                  `${t('operations.itemStatus.PREPARING')} ${formatTime(item.preparingAt, i18n.language)}`,
                                item.readyAt &&
                                  `${t('operations.itemStatus.READY')} ${formatTime(item.readyAt, i18n.language)}`,
                                item.servedAt &&
                                  `${t('operations.itemStatus.SERVED')} ${formatTime(item.servedAt, i18n.language)}`,
                              ]
                                .filter(Boolean)
                                .join(' · ') || '—'}
                            </div>
                          </TD>
                          <TD className="text-end font-medium tabular-nums">
                            {money(item.total)}
                          </TD>
                          {canCost && (
                            <TD className="text-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-expanded={costLine === item.id}
                                onClick={() =>
                                  setCostLine((current) =>
                                    current === item.id ? null : item.id,
                                  )
                                }
                              >
                                <Icon as={Coins} aria-hidden />
                                {t('operations.orders.cogs.toggle')}
                              </Button>
                            </TD>
                          )}
                        </TR>
                        {canCost && costLine === item.id && (
                          <tr className="border-b bg-subtle/60">
                            <td colSpan={columns} className="p-3">
                              <CogsDetail orderItemId={item.id} branchId={order.branchId} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )}

            <div className="space-y-1 pt-2">
              <SummaryRow label={t('common.subtotal')}>{money(totals.subtotal)}</SummaryRow>
              {/* No percentage anywhere in this block: there is no tax entity
                  and no service-charge rate to read one from. */}
              {nonZero(totals.taxTotal) && (
                <SummaryRow label={t('common.tax')}>{money(totals.taxTotal)}</SummaryRow>
              )}
              {nonZero(totals.discountTotal) && (
                <SummaryRow label={t('common.discount')} tone="danger">
                  −{money(totals.discountTotal)}
                </SummaryRow>
              )}
              {nonZero(totals.serviceTotal) && (
                <SummaryRow label={t('billing.checks.service')}>
                  {money(totals.serviceTotal)}
                </SummaryRow>
              )}
              {nonZero(totals.tipTotal) && (
                <SummaryRow label={t('common.tip')}>{money(totals.tipTotal)}</SummaryRow>
              )}
              <SummaryRule />
              <SummaryRow label={t('common.total')} tone="accent" className="text-base">
                {money(totals.total)}
              </SummaryRow>
              <p className="pt-1 text-xs text-ink-muted">
                {totals.fromCheck
                  ? t('operations.orders.totalsFromCheck')
                  : t('operations.orders.totalsOrderOnly')}
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>
                {t('operations.orders.customerDetails')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              <SummaryRow label={t('operations.orders.customer')}>
                {order.customerName ?? '—'}
              </SummaryRow>
              {order.customerPhone && (
                <SummaryRow label={t('operations.reservations.phone')}>
                  <span dir="ltr">{order.customerPhone}</span>
                </SummaryRow>
              )}
              {order.session?.tableId && (
                <SummaryRow
                  label={t('operations.orders.tableAssigned')}
                >
                  {/* `GET /orders/:id` returns a raw `tableId` cuid; the label
                      needs `GET /tables/:id`, which an order viewer need not be
                      allowed to call. Without the key, the row says there is a
                      table rather than printing an id nobody can read. */}
                  {table.data
                    ? (table.data.name ??
                      t('operations.reservations.tableRef', { number: table.data.number }))
                    : canReadTables
                      ? t('common.loading')
                      : t('nav.tables')}
                </SummaryRow>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('operations.orders.timeline')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Timeline
                nodes={steps.map((step, index) => ({
                  key: step.key,
                  label: t(`operations.orders.timelineSteps.${step.key}`),
                  meta: formatTime(step.at, i18n.language),
                  current: index === steps.length - 1,
                }))}
              />
              <p className="mt-3 text-xs text-ink-muted">
                {t('operations.orders.timelineHint')}
              </p>
            </CardContent>
          </Card>

          {canReadCheck && (
            <Card>
              <CardHeader>
                <CardTitle>
                  {t('operations.orders.paymentInfo')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                {check.loading ? (
                  <LoadingState label={t('common.loading')} />
                ) : !check.data ? (
                  <p className="text-sm text-ink-muted">
                    {t('operations.orders.noCheck')}
                  </p>
                ) : (
                  <>
                    <SummaryRow label={t('nav.checks')}>{check.data.number}</SummaryRow>
                    <SummaryRow label={t('common.status')}>
                      <Badge tone={toneForStatus(check.data.status)}>
                        {t(`operations.checkStatus.${check.data.status}`)}
                      </Badge>
                    </SummaryRow>
                    {payment ? (
                      <>
                        <SummaryRow label={t('billing.payment.method')}>
                          {t(`billing.method.${payment.method}`)}
                        </SummaryRow>
                        <SummaryRow label={t('billing.payment.amount')}>
                          {money(check.data.paidTotal)}
                        </SummaryRow>
                      </>
                    ) : (
                      <p className="text-sm text-ink-muted">{t('billing.print.noPayments')}</p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {printing && check.data && (
        <PrintableCheck check={check.data} onClose={() => setPrinting(false)} />
      )}
    </div>
  );
}
